import type {
  Organization,
  UpdateOrganizationPayload,
  CreateOrganizationPayload,
  CreateOrganizationResponse,
} from "@/types/organization";
import type {
  User,
  UserInvitation,
  UpdateUserRoleVariables,
} from "@/types/user";
import { ORG_ROLE, USER_STATUS } from "@/types/user";

import { useMockStore } from "../store";
import { generateOrganization } from "../generators/organization";

import { addTeamMemberMock } from "./team";

/**
 * Get the organization
 */
export function getOrganizationMock(): Organization | null {
  const store = useMockStore.getState();

  return store.organization;
}

/**
 * Create a new organization
 */
export function createOrganizationMock(
  payload: CreateOrganizationPayload,
): CreateOrganizationResponse {
  const store = useMockStore.getState();

  const newOrganization = {
    ...generateOrganization(),
    name: payload.name,
    phoneNumber: payload.phoneNumber ?? null,
    website: payload.website || null,
    currency: payload.currency || null,
    type: payload.type,
  };

  store.setOrganization(newOrganization);

  return {
    name: newOrganization.name,
    createdAt: newOrganization.createdAt,
    updatedAt: newOrganization.updatedAt,
  };
}

/**
 * Update the organization
 */
export function updateOrganizationMock(
  payload: UpdateOrganizationPayload,
): CreateOrganizationResponse {
  const store = useMockStore.getState();

  if (!store.organization) {
    throw new Error("Organization not found");
  }

  const updates: Partial<Organization> = {
    ...payload,
    updatedAt: new Date(),
  };

  store.updateOrganization(updates);
  const updatedOrg = { ...store.organization, ...updates };

  return {
    name: updatedOrg.name,
    createdAt: updatedOrg.createdAt,
    updatedAt: updatedOrg.updatedAt,
  };
}

/**
 * Get organization users
 */
export function getOrganizationUsersMock(): User[] {
  const store = useMockStore.getState();

  return store.users;
}

/**
 * Get pending invitations
 * @deprecated Use getOrganizationUsersMock instead - pending/expired users now come from users endpoint
 */
export function getPendingInvitationsMock(): User[] {
  const store = useMockStore.getState();

  // Return users with pending or expired status
  return store.users.filter(
    (user) => user.status === "pending" || user.status === "expired",
  );
}

/**
 * Invite a user to the organization
 */
export function inviteUserMock(payload: UserInvitation): User {
  const store = useMockStore.getState();

  if (!store.organization) {
    throw new Error("Organization not found");
  }

  if (
    store.users.some(
      (user) => user.email.toLowerCase() === payload.email.toLowerCase(),
    )
  ) {
    throw new Error(
      "This email already belongs to a member or pending invitation",
    );
  }
  const workspaces = payload.workspaces.map(
    ({ teamIds: _teamIds, ...assignment }) => {
      const workspace = store.workspaces.find(
        (item) => item.id === assignment.id,
      );
      if (!workspace) throw new Error("Workspace not found");
      return { ...assignment, name: workspace.name };
    },
  );
  const user: User = {
    id: crypto.randomUUID(),
    firstName: payload.firstName,
    lastName: payload.lastName,
    email: payload.email,
    phone: null,
    imageUrl: null,
    externalId: "",
    organizationId: store.organization.id,
    role: payload.role,
    status: USER_STATUS.PENDING,
    invitationId: crypto.randomUUID(),
    workspaces: payload.role === ORG_ROLE.ADMIN ? [] : workspaces,
    presence: { status: "offline", lastSeenAt: null },
    createdAt: new Date(),
    updatedAt: new Date(),
    deletedAt: null,
  };
  store.addUser(user);
  if (payload.role !== ORG_ROLE.ADMIN)
    payload.workspaces
      .flatMap((assignment) => assignment.teamIds ?? [])
      .forEach((teamId) => addTeamMemberMock(teamId, user.id));
  return user;
}

/**
 * Accept an invitation
 */
export function acceptInvitationMock(invitationId: string): {
  success: boolean;
} {
  console.log("[Mock] Invitation accepted:", invitationId);

  return { success: true };
}

/**
 * Join organization
 */
export function joinOrganizationMock(payload: {
  invitationId: string;
  firstName: string;
  lastName: string;
  password: string;
}): { success: boolean } {
  console.log("[Mock] User joined organization:", payload);

  return { success: true };
}

/**
 * Resend invitation
 */
export function resendInvitationMock(id: string): boolean {
  const store = useMockStore.getState();
  const user = store.users.find((item) => item.invitationId === id);
  if (!user) throw new Error("Invitation not found");
  store.updateUser(user.id, {
    status: USER_STATUS.PENDING,
    updatedAt: new Date(),
  });

  return true;
}

/**
 * Revoke invitation
 */
export function revokeInvitationMock(id: string): boolean {
  const store = useMockStore.getState();
  const user = store.users.find((item) => item.invitationId === id);
  if (!user) throw new Error("Invitation not found");
  store.deleteUser(user.id);

  return true;
}

/**
 * Update user role
 */
export function updateUserRoleMock({
  id,
  role,
  workspaces,
}: UpdateUserRoleVariables): boolean {
  const store = useMockStore.getState();

  const userIndex = store.users.findIndex((u) => u.id === id);

  if (userIndex === -1) {
    throw new Error("User not found");
  }

  const updatedUsers = [...store.users];

  updatedUsers[userIndex] = {
    ...updatedUsers[userIndex],
    ...(role ? { role } : {}),
    ...(role === ORG_ROLE.ADMIN
      ? { workspaces: [] }
      : workspaces
        ? {
            workspaces: workspaces.map((assignment) => {
              const workspace = store.workspaces.find(
                (item) => item.id === assignment.id,
              );
              if (!workspace) throw new Error("Workspace not found");
              return { ...assignment, name: workspace.name };
            }),
          }
        : {}),
    updatedAt: new Date(),
  };

  store.setUsers(updatedUsers);

  return true;
}

/**
 * Delete user from organization
 */
export function deleteUserMock(id: string): boolean {
  const store = useMockStore.getState();

  const updatedUsers = store.users.filter((u) => u.id !== id);

  store.setUsers(updatedUsers);
  console.log("[Mock] User deleted:", id);

  return true;
}
